import { useState } from 'react';
import { Button, CheckIcon, Grid, Group, Modal, Select, Text, TextInput } from '@mantine/core';
import { useSelector } from 'react-redux';
import Regex from '../../constants/Regex';
import SuperAdminBadge from '../SuperAdmin/SuperAdminBadge';
import superAdminAccents from '../SuperAdmin/superAdminAccents.module.css';

const CollaboratorModal = ({ userRole, setUserRole, close, saveAndClose }) => {
  const [isValidEmail, setIsValidEmail] = useState(true);
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user.userRoles.some((r) => r.role === 'superAdmin');

  const validateEmail = (e) => {
    const isValid = Regex.emailRegex.test(e.target.value);
    setIsValidEmail(isValid);
    handleChange(e);
  };

  const handleChange = (e) => {
    let modifiedRole = { ...userRole };
    let object = modifiedRole;
    const key = e.target.name.split('.');
    for (;;) {
      const fragment = key.shift();
      if (!fragment) {
        break;
      }

      if (typeof object[fragment] !== 'object') {
        object[fragment] = e.target.value;
        break;
      }

      object = object[fragment];
    }
    setUserRole(modifiedRole);
  };

  if (userRole === undefined) {
    return '';
  }

  const confirmText = userRole?.user?.id ? 'Save' : 'Invite';

  const roleOptions = [
    { value: 'collaborator', label: 'Collaborator' },
    { value: 'admin', label: 'Admin' },
    ...(isSuperAdmin ? [{ value: 'csm', label: 'Customer Success Manager' }] : []),
  ];

  return (
    <Modal opened={!!userRole} onClose={() => close()} title="Add Collaborator">
      <div data-tour="accountSettingsCollaboratorsTabAddButtonForm">
        <Grid mb="sm">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>First Name</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="user.firstName"
              disabled={userRole.id}
              value={userRole.user.firstName || ''}
              onChange={handleChange}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="sm">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Last Name</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="user.lastName"
              disabled={userRole.id}
              value={userRole.user.lastName || ''}
              onChange={handleChange}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="sm">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Email</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="user.email"
              disabled={userRole.id}
              value={userRole.user.email || ''}
              onChange={validateEmail}
              error={
                !isValidEmail
                  ? 'Email is not valid. Please ensure that the email address you entered is valid and does not contain any spaces.'
                  : null
              }
            />
          </Grid.Col>
        </Grid>
        <Grid mb="sm">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Phone Number</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="user.phoneNumber"
              disabled={userRole.id}
              value={userRole.user.phoneNumber || ''}
              onChange={handleChange}
              placeholder="+1234567890"
            />
          </Grid.Col>
        </Grid>
        <Grid mb="sm">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Role</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Select
              name="role"
              value={userRole.role}
              onChange={(value) => handleChange({ target: { name: 'role', value } })}
              data={roleOptions}
              classNames={{ option: superAdminAccents.option }}
              renderOption={({ option, checked }) => (
                <Group gap="xs" justify="space-between" flex={1}>
                  {/* A custom renderOption replaces the whole option body, so the
                      selected-value check mark has to be rendered back by hand. */}
                  <Group gap="xs">
                    {checked && <CheckIcon size={12} />}
                    <span>{option.label}</span>
                  </Group>
                  {option.value === 'csm' && <SuperAdminBadge show tooltip="Only super admins can assign this role" />}
                </Group>
              )}
            />
          </Grid.Col>
        </Grid>
      </div>
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={() => close()}>
          Close
        </Button>
        <Button
          disabled={
            !userRole ||
            !userRole.role ||
            !userRole.user ||
            !userRole.user.email ||
            !userRole.user.firstName ||
            !userRole.user.lastName ||
            !isValidEmail
          }
          data-tour="collaboratorsModalSaveBtn"
          onClick={() => saveAndClose(userRole)}
        >
          {confirmText}
        </Button>
      </Group>
    </Modal>
  );
};

export default CollaboratorModal;
